import React, { useEffect, useState } from "react";
import {
    Box,
    Modal,
    Typography,
    IconButton,
    Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import { useThemeContext } from "../../../Context/ThemeContext";
import { useTranslation } from "react-i18next";
import TempImage from '../../../assets/TempProductImage.webp'
import ConfirmationModal from "../../../Components/Common/ConfirmationModal";
import ProductSelectionModal from "./ProductSelectionModal";
import useMediaQuery from '@mui/material/useMediaQuery';
import { Tooltip } from "@mui/material";
const ProductModal1 = ({ open, onClose, onSave, error, tags, refreshSlots }) => {
    const { t } = useTranslation();
    const { mode } = useThemeContext();
    const [availableProducts, setAvailableProducts] = useState(0);
    const [totalProducts, setTotalProducts] = useState(0);
    const [isModified, setIsModified] = useState(false);
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const [showProductSelectionModal, setShowProductSelectionModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [localError, setLocalError] = useState("");

    const btnColor = mode === 'dark' ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
    const inputBoxColor = mode === 'dark' ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
    const textborderColor = mode === 'dark' ? DARKCOLORS.GreyScale40 : LIGHTCOLORS.GreyScale40;
    const cardColor = mode === 'dark' ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;

    const openProductSelectionModal = () => setShowProductSelectionModal(true);
    const closeProductSelectionModal = () => setShowProductSelectionModal(false);

    // Sync availableProducts when totalProducts drops below it
    useEffect(() => {
        if (availableProducts > totalProducts) {
            setAvailableProducts(totalProducts);
        }
    }, [totalProducts]);

    // Handlers for increment and decrement
    const handleIncrement = (setter, value) => {
        setter(value + 1);
        setIsModified(true);
    };

    const handleDecrement = (setter, value) => {
        setter(value > 0 ? value - 1 : 0);
        setIsModified(true);
    };

    const handleSave = async () => {
        // ponytail: frontend validation — instant feedback, skip wasted API call
        if (!selectedProduct?.productId) {
            setLocalError(t("vendingMachines.addProductFirst"));
            return;
        }
        if (availableProducts > totalProducts) {
            setLocalError(t("vendingMachines.quantityExceedsCapacity"));
            return;
        }
        if (availableProducts === 0) {
            setLocalError(t("vendingMachines.availableProductsMinRequired"));
            return;
        }
        setLocalError("");
        try {
            // Call the onSave function with updated values
            await onSave({
                productId: selectedProduct?.productId || null,
                quantity: availableProducts,
                capacity: totalProducts
            });

            setIsModified(false); // Reset the modified flag on save
            setShowDiscardModal(false);
            onClose(); // Close the modal
            setSelectedProduct(null)
            setAvailableProducts(0)
            setTotalProducts(0)
        } catch (error) {
            console.error(t("vendingMachines.errorSavingProductData"), error);
            // Error is already displayed via MachineDetails' setError
        }
    };

    const handleModalClose = () => {
        if (isModified) {
            setShowDiscardModal(true);
        } else {
            resetFormState();
            onClose();
        }
    };

    const handleDiscard = () => {
        resetFormState();
        onClose();
        refreshSlots()
    };

    // Ponytail: single reset function to keep all close paths consistent
    const resetFormState = () => {
        setAvailableProducts(0);
        setTotalProducts(0);
        setIsModified(false);
        setSelectedProduct(null);
        setLocalError("");
        setShowDiscardModal(false);
    };

    const handleProductSelect = (product) => {
        setSelectedProduct(product);
        setIsModified(true);
        setLocalError(""); // Clear any previous validation error
        closeProductSelectionModal(); // Close the product selection modal
    };

    // Function to get the tag icon based on the label
    const getTagIcon = (tagLabel) => {
        const tag = tags.find(tag => tag.label.toLowerCase() === tagLabel.toLowerCase());
        if (!tag || !tag.IconComponent) return null;

        const Icon = tag.IconComponent;
        const noSlash = ['vegan', 'vegetarian', 'high protein'].includes(tagLabel.toLowerCase());

        return (
            <span
                style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    padding: '5px',
                    backgroundColor: 'white', // or use theme
                    overflow: 'hidden',
                }}
            >
                <Icon width="20" height="20" />

                {!noSlash && (
                    <span
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            width: '100%',
                            height: '1px',
                            backgroundColor: 'black', // Adjust to contrast with background
                            transform: 'translate(-50%, -50%) rotate(130deg)',
                            transformOrigin: 'center',
                        }}
                    />
                )}
            </span>
        );
    };
    const isMobile = useMediaQuery('(max-width:610px)');

    return (
        <>
            <Modal open={open} onClose={handleModalClose} closeAfterTransition>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        // width: 500,
                        bgcolor: cardColor,
                        borderRadius: '12px',
                        boxShadow: 24,
                        p: 4,
                        '@media (max-width: 600px)': {
                            width: "80%",
                            p: 2,
                        },
                        '@media (max-width: 375px)': {
                            width: "100%",
                            p: 2,
                        },
                    }}>
                    <Box sx={{ width: "100%", }}>
                        {/* Header */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                            <h2 className="addmachine-text-heading1" style={{ margin: "0px" }}></h2>
                            <IconButton sx={{ p: 0 }} onClick={handleModalClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        {/* Product Image */}
                        <Box sx={{ width: "100%", textAlign: "center", mb: 1, position: 'relative' }}>
                            <img
                                src={selectedProduct?.imageUrl || TempImage}
                                alt={selectedProduct?.productName}
                                onError={(e) => {
                                    e.target.onerror = null; // Prevent infinite loop
                                    e.target.src = TempImage;
                                }}
                                style={{
                                    width: isMobile ? "100%" : "520px",
                                    height: isMobile ? "100%" : "250px",
                                    objectFit: "cover",
                                    borderRadius: "15px",
                                }}
                            />
                            {/* Tags: Icons at the bottom-right corner */}
                            {/* {selectedProduct?.tags && selectedProduct?.tags?.length > 0 && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        bottom: "15px",
                                        right: "10px",
                                        display: "flex",
                                        gap: "5px",
                                    }}
                                >
                                    {selectedProduct?.tags.map((tag, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                // backgroundColor: "#fff",
                                                // borderRadius: "50%",
                                                // padding: "5px",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                            }}
                                        >
                                            {getTagIcon(tag)}
                                        </Box>
                                    ))}
                                </Box>
                            )} */}
                        </Box>

                        {/* Product Name */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem", gap: "10px" }}>
                            <Tooltip
                                title={selectedProduct?.productName || ""}
                                arrow
                                placement="top"
                            >
                                <p className="machine-name-text " id="asiles_product_name" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "200px" }}>
                                    {selectedProduct?.productName}
                                </p>
                            </Tooltip>
                            <button onClick={openProductSelectionModal} type="button" className="view-more-btn" style={{ background: "transparent", color: btnColor, borderColor: btnColor }}>
                                {selectedProduct?.productName ? t('vendingMachines.changeProduct') : t('inventory.addProduct')}
                            </button>
                        </Box>

                        {/* Stock Management */}
                        <Box sx={{ display: "flex", justifyContent: "space-around", alignItems: "center", my: 5 }}>
                            {/* Present Available Products */}
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 1.5 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <IconButton
                                        sx={{
                                            backgroundColor: inputBoxColor,
                                            borderRadius: "50%",
                                            padding: 0,
                                            width: 25, // Adjust the size of the circle
                                            height: 25, // Adjust the size of the circle
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            "&:hover": {
                                                backgroundColor: inputBoxColor,
                                            },
                                        }}
                                        onClick={() => {
                                            setLocalError("");
                                            handleDecrement(setAvailableProducts, availableProducts);
                                        }}>
                                        <Typography style={{ fontSize: "15px", marginTop: "-3px" }} variant="h6">-</Typography>
                                    </IconButton>
                                    <Box
                                        sx={{
                                            minWidth: 60,
                                            backgroundColor: textborderColor,
                                            borderRadius: "6px",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            padding: "9px 22px 9px 21px",
                                        }}
                                    >
                                        <p id="machineStreet1" className="popup-text" style={{ margin: "0px" }}>{availableProducts.toString().padStart(2, "0")}</p>
                                    </Box>
                                    <IconButton
                                        sx={{
                                            backgroundColor: inputBoxColor,
                                            borderRadius: "50%",
                                            padding: 0,
                                            width: 25, // Adjust the size of the circle
                                            height: 25, // Adjust the size of the circle
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            "&:hover": {
                                                backgroundColor: inputBoxColor,
                                            },
                                        }}
                                        onClick={() => {
                                            if (availableProducts >= totalProducts) {
                                                setLocalError(t("vendingMachines.quantityExceedsCapacity"));
                                                return;
                                            }
                                            setLocalError("");
                                            handleIncrement(setAvailableProducts, availableProducts);
                                        }}>
                                        <Typography style={{ fontSize: "15px", marginTop: "-3px" }} variant="h6">+</Typography>
                                    </IconButton>
                                </Box>
                                <p id="machineStreet1" className="add-machine" style={{ width: "130px", textAlign: "center", whiteSpace: 'wrap' }}>{t("Present available products")}</p>
                            </Box>

                            {/* Divider */}
                            <Divider orientation="vertical" flexItem sx={{ transform: "rotate(15deg)" }} />

                            {/* Total Stocked Products */}
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 1.5 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <IconButton
                                        sx={{
                                            backgroundColor: inputBoxColor,
                                            borderRadius: "50%",
                                            padding: 0,
                                            width: 25, // Adjust the size of the circle
                                            height: 25, // Adjust the size of the circle
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            "&:hover": {
                                                backgroundColor: inputBoxColor,
                                            },
                                        }}
                                        onClick={() => {
                                            setLocalError("");
                                            handleDecrement(setTotalProducts, totalProducts);
                                        }}>
                                        <Typography style={{ fontSize: "15px", marginTop: "-3px" }} variant="h6">-</Typography>
                                    </IconButton>
                                    <Box
                                        sx={{
                                            minWidth: 60,
                                            backgroundColor: textborderColor,
                                            borderRadius: "6px",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            padding: "9px 22px 9px 21px",
                                        }}
                                    >
                                        <p id="machineStreet1" className="popup-text" style={{ margin: "0px" }}>{totalProducts.toString().padStart(2, "0")}</p>
                                    </Box>
                                    <IconButton
                                        sx={{
                                            backgroundColor: inputBoxColor,
                                            borderRadius: "50%",
                                            padding: 0,
                                            width: 25, // Adjust the size of the circle
                                            height: 25, // Adjust the size of the circle
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            "&:hover": {
                                                backgroundColor: inputBoxColor,
                                            },
                                        }}
                                        onClick={() => {
                                            setLocalError("");
                                            handleIncrement(setTotalProducts, totalProducts);
                                        }}>
                                        <Typography style={{ fontSize: "15px", marginTop: "-3px" }} variant="h6">+</Typography>
                                    </IconButton>
                                </Box>
                                <p id="machineStreet1" className="add-machine" style={{ width: "130px", textAlign: "center", whiteSpace: 'wrap' }}>{t("Total stocked products")}</p>
                            </Box>
                        </Box>

                        {(error || localError) && (
                            <Typography color="error" variant="body2" sx={{ textAlign: "center", marginBottom: 1.5 }}>
                                {localError || error}
                            </Typography>
                        )}

                        {/* Actions */}
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <button type="button" style={{ background: btnColor }} onClick={handleSave} className="save-btn">{t('membershipCards.save')}</button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            {/* Use the ProductSelectionModal */}
            <ProductSelectionModal
                open={showProductSelectionModal}
                onClose={closeProductSelectionModal}
                onSelect={handleProductSelect}
                tags={tags}
            />

            {/* Discard Confirmation Modal */}
            <ConfirmationModal
                open={showDiscardModal}
                onClose={() => setShowDiscardModal(false)}
                onSave={handleSave}
                onDiscard={handleDiscard}
            />
        </>
    );
};

export default ProductModal1;
